@if (loadingComputed()) {
  <!-- Loading State -->
  <div class="loading-state">
    <div class="spinner"></div>
    <p>Loading pricing information...</p>
  </div>
} @else if (errorComputed()) {
  <!-- Error State -->
  <div class="error-state">
    <div class="error-icon">⚠️</div>
    <p>{{ errorComputed() }}</p>
    <button class="retry-button" (click)="refreshData()">Retry</button>
  </div>
} @else {
  <!-- Main Content -->
  <div class="pricing-content rounded-2xl" [ngClass]="isMobile() ? 'p-0' : 'p-6'">

    <!-- Main Layout -->
    @if (hasProducts()) {
      @if (isMobile()) {
        <!-- Mobile Layout: Tabs on top, content below (per Figma 31-1202) -->
        <div class="flex flex-col max-w-full">
          <!-- Top: Horizontal scrolling tabs -->
          <div class="bg-white">
            <product-tabs 
              [showPricing]="false"
              (onProductSelect)="onProductSelect($event)">
            </product-tabs>
          </div>
          
          <!-- Content: Plan cards with purple background -->
          <main class="bg-brand-purple-50 p-4">
            @if (selectedProductComputed()) {
              <div class="space-y-4">
                @if (selectedProductComputed()?.plans) {
                  @for (mainKey of ['main']; track mainKey) {
                    <plan-display
                      [componentKey]="mainKey"
                      [plans]="selectedProductComputed()?.plans || []"
                      [availableRegions]="availableRegions()"
                      [showPopularBadge]="true"
                      [showYearlyPricing]="true"
                      [allowRegionSelection]="allowRegionSelection()"
                      [allowRangedValueSelection]="allowRangedValueSelection()"
                      displayMode="cards"
                      featuresMode="highlighted"
                      (onPlanSelect)="onPlanSelected($event)"
                      (onRegionChange)="onRegionChange.emit($event)"
                      (onRangedValueChange)="onRangedValueChange.emit($event)">
                    </plan-display>
                  }

                  <!-- Show Full Comparison Button -->
                  <div class="text-center pt-2">
                    <button
                      class="inline-flex items-center gap-2 text-brand-purple-600 hover:text-brand-purple-700 font-semibold text-sm transition-colors py-2 px-4"
                      (click)="openComparison()">
                      Show full comparison
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                      </svg>
                    </button>
                  </div>
                } @else {
                  <div class="text-center py-8 bg-white rounded-lg">
                    <p class="text-gray-500 text-sm">No plans available for this product.</p>
                  </div>
                }
              </div>
            } @else {
              <div class="text-center py-8 bg-gray-50 rounded-lg">
                <p class="text-gray-500 text-sm">No pricing data available.</p>
              </div>
            }
          </main>
        </div>
      } @else {
        <!-- Desktop Layout: Sidebar + Content (per Figma) -->
        <!-- Horizontal product tabs for tablet (640-1023px) — hidden at 1024px+ where sidebar takes over -->
        <div [ngClass]="topTabs() ? '' : 'lg:hidden'">
          <product-tabs
            [forceHorizontal]="topTabs()"
            [showPricing]="false"
            (onProductSelect)="onProductSelect($event)">
          </product-tabs>
        </div>
        <div class="flex w-full">
          <!-- Left Sidebar — visible at 1024px+ only (uses signal, not CSS hidden/lg:block which shell !important breaks) -->
          @if (isDesktop() && !topTabs()) {
            <aside class="w-56 flex-shrink-0">
              <product-tabs
                [showPricing]="false"
                (onProductSelect)="onProductSelect($event)">
              </product-tabs>
            </aside>
          }

          <!-- Main Content: Plan Card Container with purple background -->
          <main class="flex-1 min-w-0 bg-brand-purple-50 rounded-2xl p-6">
            @if (selectedProductComputed()) {
              <div class="space-y-6">
                <!-- Plans Display Component -->
                @if (selectedProductComputed()?.plans) {
                  @for (mainKey of ['main']; track mainKey) {
                    <plan-display
                      [componentKey]="mainKey"
                      [plans]="selectedProductComputed()?.plans || []"
                      [availableRegions]="availableRegions()"
                      [showPopularBadge]="true"
                      [showYearlyPricing]="true"
                      [allowRegionSelection]="allowRegionSelection()"
                      [allowRangedValueSelection]="allowRangedValueSelection()"
                      displayMode="cards"
                      featuresMode="highlighted"
                      (onPlanSelect)="onPlanSelected($event)"
                      (onRegionChange)="onRegionChange.emit($event)"
                      (onRangedValueChange)="onRangedValueChange.emit($event)">
                    </plan-display>
                  }

                  <!-- Show Full Comparison Button -->
                  <div class="text-center pt-2">
                    <button
                      class="inline-flex items-center gap-2 text-brand-purple-600 hover:text-brand-purple-700 font-semibold text-base transition-colors py-2 px-4"
                      (click)="openComparison()">
                      Show full comparison
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                      </svg>
                    </button>
                  </div>
                } @else {
                  <div class="text-center py-12 bg-white rounded-lg">
                    <p class="text-gray-500">No plans available for this product.</p>
                  </div>
                }
              </div>
            }
          </main>
        </div>
      }
    }
    <!-- Comparison Modal - Use timestamp to force recreation -->
    @if (showComparisonModal()) {
      <comparison-modal
        class="visible"
        [modalKey]="modalOpenTimestamp()"
        [availableRegions]="availableRegions()"
        (onClose)="closeComparison()"
        (onPlanSelect)="onPlanSelected($event)"
        (onRegionChange)="onRegionChange.emit($event)"
        (onRangedValueChange)="onRangedValueChange.emit($event)">
      </comparison-modal>
    }
  </div>
}
